import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { Label, Select, SearchableSelect } from '@/shared/components';
import { PlanVehicleRuleFourByFourField } from './PlanVehicleRuleFourByFourField';
import type { transformFilteredOptions } from '@/features/vehiclesearch/utils';
import type {
    VehicleSearchFilters,
    SelectFilterOption,
} from '@/features/vehiclesearch/types/manualsearch.types';
import type { PlanVehicleRuleFormInput, PlanVehicleRuleFormOutput } from '../schemas/planVehicleRule.schema';
import { PlanVehicleRuleDerivativeField, PlanVehicleRuleModelField } from './PlanVehicleRuleModelDerivativeFields';

interface Props {
    control: Control<PlanVehicleRuleFormInput, unknown, PlanVehicleRuleFormOutput>;
    derivativeOptions: Pick<ReturnType<typeof transformFilteredOptions>, 'makes' | 'models'>;
    derivativeSelectOptions: SelectFilterOption[];
    // Built by useVehicleRestrictionOptions: the Any sentinel, the active list, and any pinned saved id.
    transmissionOptions: SelectFilterOption[];
    fuelTypeOptions: SelectFilterOption[];
    makeValue: SelectFilterOption | null;
    modelValue: SelectFilterOption | null;
    derivativeValue: SelectFilterOption | null;
    filters: VehicleSearchFilters;
    onFilterChange: (key: keyof VehicleSearchFilters, value: string) => void;
    matchingDerivativeCount: number;
    derivativeSelected: boolean;
    hasMake: boolean;
    hasMakeAndModel: boolean;
    isLoadingOptions: boolean;
    isLoadingTransmissions: boolean;
    isLoadingFuelTypes: boolean;
    busy: boolean;
}

export const PlanVehicleRuleVehicleFields: React.FC<Props> = ({
    control,
    derivativeOptions,
    derivativeSelectOptions,
    transmissionOptions,
    fuelTypeOptions,
    makeValue,
    modelValue,
    derivativeValue,
    filters,
    onFilterChange,
    matchingDerivativeCount,
    derivativeSelected,
    hasMake,
    hasMakeAndModel,
    isLoadingOptions,
    isLoadingTransmissions,
    isLoadingFuelTypes,
    busy,
}) => (
    <>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
                <Label htmlFor="pvr_make_id">
                    Make <span className="text-mp-muted">(optional)</span>
                </Label>
                <SearchableSelect<SelectFilterOption>
                    id="pvr_make_id"
                    options={derivativeOptions.makes}
                    value={makeValue}
                    onChange={(o) => onFilterChange('make_id', o?.value ?? '')}
                    getOptionValue={(o) => o.value}
                    getOptionLabel={(o) => o.label}
                    // Disabled until makes load, or while the first fetch is still in flight.
                    // `busy` also freezes the whole surface while a save is in flight, so no
                    // edit can be made that the already-sent payload does not carry.
                    disabled={busy || derivativeOptions.makes.length === 0 || (isLoadingOptions && !filters.make_id)}
                    clearable
                    placeholder="Any make"
                    emptyLabel="No makes available."
                />
            </div>

            <PlanVehicleRuleModelField
                options={derivativeOptions.models}
                modelValue={modelValue}
                onChange={(modelId) => onFilterChange('model_id', modelId)}
                derivativeSelected={derivativeSelected}
                disabled={busy || !hasMake || derivativeSelected || (isLoadingOptions && !filters.model_id)}
                placeholder={!hasMake ? 'Select a make first' : 'Any model'}
            />
        </div>

        <PlanVehicleRuleDerivativeField
            control={control}
            options={derivativeSelectOptions}
            derivativeValue={derivativeValue}
            disabled={busy || !hasMakeAndModel || matchingDerivativeCount === 0}
            placeholder={!hasMakeAndModel ? 'Select make and model first' : 'Any derivative'}
        />

        <div className="grid grid-cols-2 gap-3">
            <div>
                <Label htmlFor="pvr_transmission_id">
                    Transmission <span className="text-mp-muted">(optional)</span>
                </Label>
                <Select
                    id="pvr_transmission_id"
                    options={transmissionOptions}
                    value={derivativeSelected ? '' : (filters.transmission_id ?? '')}
                    onChange={(value) => onFilterChange('transmission_id', value)}
                    // "Any" alone is nothing to choose between, so the list is only usable past one
                    // entry; a pinned saved option also keeps it past the auto-select threshold.
                    disabled={busy || derivativeSelected || isLoadingTransmissions || transmissionOptions.length <= 1}
                    placeholder="Any"
                    ariaLabel="Transmission"
                    isLoading={isLoadingTransmissions}
                />
            </div>

            <div>
                <Label htmlFor="pvr_fuel_type_id">
                    Fuel type <span className="text-mp-muted">(optional)</span>
                </Label>
                <Select
                    id="pvr_fuel_type_id"
                    options={fuelTypeOptions}
                    value={derivativeSelected ? '' : (filters.fuel_type_id ?? '')}
                    onChange={(value) => onFilterChange('fuel_type_id', value)}
                    disabled={busy || derivativeSelected || isLoadingFuelTypes || fuelTypeOptions.length <= 1}
                    placeholder="Any"
                    ariaLabel="Fuel type"
                    isLoading={isLoadingFuelTypes}
                />
            </div>
        </div>

        <Controller
            name="requires_4x4"
            control={control}
            render={({ field }) => (
                <PlanVehicleRuleFourByFourField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={busy || derivativeSelected}
                />
            )}
        />
    </>
);
